import { useEffect, useMemo, useState } from "react";
import Button from "@/components/base/Button";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import EmptyState from "@/pages/admin/components/EmptyState";
import AfiliadoEstadoBadge from "@/pages/admin/components/AfiliadoEstadoBadge";
import FiltrosAfiliados from "./components/FiltrosAfiliados";
import AfiliadoDetalle from "./components/AfiliadoDetalle";
import { useAfiliados } from "@/hooks/useAfiliados";
import { supabase } from "@/lib/supabase";
import { formatFecha } from "@/lib/format";
import { tipoAfiliadoLabel } from "@/lib/affiliateEstados";
import type { Afiliado } from "@/types";

export default function AdminAfiliados() {
  const { afiliados, loading, error, reload, setAfiliados } = useAfiliados();
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [tipo, setTipo] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error: queryError } = await supabase
          .from("affiliate_vehicles")
          .select("affiliate_id");
        if (queryError) throw queryError;
        const map: Record<string, number> = {};
        (data as { affiliate_id: string }[]).forEach((row) => {
          map[row.affiliate_id] = (map[row.affiliate_id] ?? 0) + 1;
        });
        if (active) setCounts(map);
      } catch {
        /* la cuenta de vehículos es opcional */
      }
    })();
    return () => {
      active = false;
    };
  }, [afiliados.length]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return afiliados.filter((item) => {
      if (estado && item.estado !== estado) return false;
      if (tipo && item.tipo !== tipo) return false;
      if (!term) return true;
      return [
        item.numero,
        item.nombre_completo,
        item.empresa_nombre_comercial,
        item.empresa_razon_social,
        item.email,
        item.telefono,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [afiliados, q, estado, tipo]);

  const selected = useMemo(
    () => afiliados.find((item) => item.id === selectedId) ?? null,
    [afiliados, selectedId],
  );

  const clearFilters = () => {
    setQ("");
    setEstado("");
    setTipo("");
  };

  const handleEstadoChange = (id: string, nuevoEstado: string) => {
    setAfiliados((list) =>
      list.map((item) => (item.id === id ? { ...item, estado: nuevoEstado as Afiliado["estado"] } : item)),
    );
  };

  const nombreDe = (item: Afiliado) =>
    item.tipo === "empresa"
      ? item.empresa_nombre_comercial || item.empresa_razon_social || "Empresa"
      : item.nombre_completo || "Sin nombre";

  return (
    <div>
      <AdminPageHeader
        title="Afiliados"
        description="Solicitudes de afiliación de propietarios, conductores y empresas de transporte que quieren unirse a la red de AXONIA GROUP."
        action={
          <Button variant="outline" size="md" icon="ri-refresh-line" onClick={reload} disabled={loading}>
            Actualizar
          </Button>
        }
      />

      {error ? (
        <div className="flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-wifi-off-line text-2xl leading-none" aria-hidden="true"></i>
          </span>
          <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
            No se pudieron cargar las afiliaciones
          </h3>
          <p className="mt-1.5 max-w-md text-sm text-foreground-600">{error}</p>
          <Button variant="primary" size="md" icon="ri-refresh-line" onClick={reload} className="mt-5">
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <FiltrosAfiliados
            q={q}
            estado={estado}
            tipo={tipo}
            resultCount={filtered.length}
            total={afiliados.length}
            onQ={setQ}
            onEstado={setEstado}
            onTipo={setTipo}
            onClear={clearFilters}
          />

          <div className="mt-4">
            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2, 3].map((key) => (
                  <div key={key} className="h-16 animate-pulse rounded-lg border border-background-200 bg-background-100"></div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon="ri-user-shared-line"
                title={afiliados.length === 0 ? "Todavía no hay afiliaciones" : "Sin resultados"}
                description={
                  afiliados.length === 0
                    ? "Cuando alguien envíe el formulario de 'Trabaja con nosotros', aparecerá aquí."
                    : "No encontramos afiliaciones con los filtros aplicados. Prueba con otros criterios."
                }
                action={
                  afiliados.length > 0 ? (
                    <Button variant="outline" size="md" icon="ri-filter-off-line" onClick={clearFilters}>
                      Limpiar filtros
                    </Button>
                  ) : null
                }
              />
            ) : (
              <>
                {/* Tabla (escritorio) */}
                <div className="hidden overflow-hidden rounded-lg border border-background-200 bg-background-50 md:block">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[920px] text-left">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100 text-xs font-label uppercase tracking-wide text-foreground-500">
                          <th className="px-4 py-3 font-semibold">Número</th>
                          <th className="px-4 py-3 font-semibold">Nombre / Empresa</th>
                          <th className="px-4 py-3 font-semibold">Tipo</th>
                          <th className="px-4 py-3 font-semibold">Teléfono</th>
                          <th className="px-4 py-3 font-semibold">Ciudad</th>
                          <th className="px-4 py-3 font-semibold">Vehículos</th>
                          <th className="px-4 py-3 font-semibold">Estado</th>
                          <th className="px-4 py-3 font-semibold">Fecha</th>
                          <th className="px-4 py-3 text-right font-semibold">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-200">
                        {filtered.map((item) => (
                          <tr key={item.id} className="transition-colors hover:bg-background-100">
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-label font-semibold text-primary-700">
                              {item.numero}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-foreground-900">{nombreDe(item)}</p>
                              <p className="text-xs text-foreground-500">{item.email}</p>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-700">
                              {tipoAfiliadoLabel(item.tipo)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-600">
                              {item.telefono ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground-600">{item.ciudad ?? "—"}</td>
                            <td className="px-4 py-3 text-sm text-foreground-600">
                              {counts[item.id] ?? 0}
                            </td>
                            <td className="px-4 py-3">
                              <AfiliadoEstadoBadge estado={item.estado} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-500">
                              {formatFecha(item.creado_en)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => setSelectedId(item.id)}
                                className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                              >
                                <i className="ri-eye-line text-base leading-none" aria-hidden="true"></i>
                                Ver
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tarjetas (móvil) */}
                <ul className="space-y-3 md:hidden">
                  {filtered.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(item.id)}
                        className="w-full cursor-pointer rounded-lg border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-primary-300"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm font-label font-semibold text-primary-700">
                            {item.numero}
                          </span>
                          <AfiliadoEstadoBadge estado={item.estado} />
                        </div>
                        <p className="mt-2 text-sm font-medium text-foreground-900">{nombreDe(item)}</p>
                        <p className="mt-0.5 text-xs text-foreground-500">
                          {tipoAfiliadoLabel(item.tipo)} · {counts[item.id] ?? 0} vehículos ·{" "}
                          {formatFecha(item.creado_en)}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}

      {selected && (
        <AfiliadoDetalle
          afiliado={selected}
          onClose={() => setSelectedId(null)}
          onEstadoChange={handleEstadoChange}
        />
      )}
    </div>
  );
}